
import React, { useState, useEffect, useMemo } from 'react';
import { dailyChallengePuzzles as mockDailyChallenges } from '../mockData';
import { usePuzzles } from '../PuzzleContext';
import PostCard from '../components/PostCard';
import BackButton from '../components/BackButton';
import { useAuth } from '../AuthContext';
import { useAdmin } from '../AdminContext';
import { db } from '../firebase'; // Import DB to fetch daily challenges
import { Puzzle } from '../types';

const DailyChallengePage: React.FC = () => {
    const { reportPuzzle } = usePuzzles();
    const { userProfile, updateSettings } = useAuth();
    const { addNotification } = useAdmin();
    const [timeLeft, setTimeLeft] = useState('');
    const [rewardClaimed, setRewardClaimed] = useState(false);
    const [dailyPuzzles, setDailyPuzzles] = useState<Puzzle[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    // Fetch Daily Puzzles from Firestore
    useEffect(() => {
        const fetchDailyChallenges = async () => {
            setIsLoading(true);
            const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
            try {
                const snapshot = await db.collection('daily_challenges')
                    .where('dailyDate', '==', today)
                    .get();

                if (!snapshot.empty) {
                    const loadedPuzzles = snapshot.docs.map((doc: any) => ({
                        id: doc.id,
                        ...doc.data()
                    })) as Puzzle[];
                    setDailyPuzzles(loadedPuzzles);
                } else {
                    // Fallback to mock data if no live challenge exists for today
                    setDailyPuzzles(mockDailyChallenges);
                }
            } catch (error) {
                console.error("Error fetching daily challenges:", error);
                setDailyPuzzles(mockDailyChallenges);
            } finally {
                setIsLoading(false);
            }
        };

        fetchDailyChallenges();
    }, []);

    const solvedChallenges = useMemo(() => {
        if (!userProfile) return [];
        return dailyPuzzles.filter(p => userProfile.solvedPuzzles.includes(p.id));
    }, [userProfile?.solvedPuzzles, dailyPuzzles]);

    const solvedCount = solvedChallenges.length;
    const allChallengesCompleted = dailyPuzzles.length > 0 && solvedCount === dailyPuzzles.length;

    useEffect(() => {
        if (allChallengesCompleted && !rewardClaimed && userProfile) {
            const cluesBefore = userProfile.clues;
            const newClueCount = cluesBefore + 2;
            updateSettings({ clues: newClueCount });
            addNotification(userProfile.uid, {
                type: 'guess_correct', 
                text: 'You completed all daily challenges and earned 2 bonus clues!',
            });
            setRewardClaimed(true);
        }
    }, [allChallengesCompleted, rewardClaimed, userProfile, updateSettings, addNotification]);
    
    useEffect(() => {
        const calculateTimeLeft = () => {
            const now = new Date();
            const midnight = new Date(now);
            midnight.setHours(24, 0, 0, 0); // Next midnight
            const diff = midnight.getTime() - now.getTime();

            const hours = String(Math.floor((diff / (1000 * 60 * 60)) % 24)).padStart(2, '0');
            const minutes = String(Math.floor((diff / 1000 / 60) % 60)).padStart(2, '0');
            const seconds = String(Math.floor((diff / 1000) % 60)).padStart(2, '0');

            return `${hours}:${minutes}:${seconds}`;
        };

        const timerId = setInterval(() => {
            setTimeLeft(calculateTimeLeft());
        }, 1000);
        
        setTimeLeft(calculateTimeLeft()); // Initial call

        return () => clearInterval(timerId);
    }, []);

    return (
        <div className="space-y-4">
            <BackButton text="Back Home" />

            <div className="p-6 rounded-xl text-center text-white bg-gradient-to-r from-yellow-400 to-orange-500 shadow-lg">
                <h1 className="text-3xl font-bold">Today's Challenges</h1>
                <p className="text-white/80 mt-1">Complete all 3 puzzles to earn 2 bonus clues!</p>
            </div>
            
            <div className="bg-white p-4 rounded-xl shadow-sm flex flex-col sm:flex-row justify-between items-center space-y-2 sm:space-y-0">
                <div className="font-bold text-gray-700">
                    Progress: <span className="text-lg text-[var(--primary-dark-purple)]">{solvedCount} / {dailyPuzzles.length} Completed</span>
                </div>
                {allChallengesCompleted ? (
                     <div className="font-bold text-lg bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full flex items-center gap-2">
                        <i className="ph-duotone ph-check-circle text-emerald-600"></i>
                        <span>Reward Claimed! +2 Clues</span>
                    </div>
                ) : (
                    <div className="font-mono font-bold text-lg bg-gray-800 text-white px-3 py-1 rounded-md">
                        <span>Next Challenge In: {timeLeft}</span>
                    </div>
                )}
            </div>
            
            <div className="space-y-4">
                {isLoading ? (
                    <div className="flex justify-center py-12">
                        <div className="spinner-white" style={{ borderColor: 'rgba(107, 33, 168, 0.3)', borderLeftColor: 'var(--primary-dark-purple)' }}></div>
                    </div>
                ) : dailyPuzzles.length > 0 ? (
                    dailyPuzzles.map(puzzle => (
                         <PostCard 
                            key={puzzle.id}
                            puzzle={puzzle} 
                            isClickable={false} 
                            onReport={reportPuzzle}
                        />
                    ))
                ) : (
                    <p className="text-center text-gray-500 py-8">No challenges for today yet.</p>
                )}
            </div>
        </div>
    );
};

export default DailyChallengePage;
